<script lang="ts">
	//Design System
	import {
		EnergyDistributionZendure,
		ControlsContainer,
		ParameterCardV2,
		ParameterCardGroupV2,
	} from '@Shelly/shelly-design-system';
	//Types
	import type { Device } from 'src/types/Device';
	import type { EnergyDistributionZendurePropsTypes } from '@Shelly/shelly-design-system';
	//Stores
		import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { real_id } from 'src/helpers/interface_helpers';
	import { capFirstChar, convert } from 'src/helpers/utilities';
	import { getBatteryIcon } from 'src/helpers/device_data_helpers';
	import { format } from 'svelte-i18n';

	export let device: Device;
	$: statusStore = getDeviceStatus(device.id);
	$: status = $statusStore;

	// Solar
	$: pvPower1 = status?.pvPower1 || 0;
	$: pvPower2 = status?.pvPower2 || 0;
	$: solarInputPower = status?.solarInputPower || 0;
	// Home
	$: outputHomePower = status?.outputHomePower || 0;
	// Battery
	$: outputToPackPower = status?.outputPackPower || 0;
	$: packInputPower = status?.packInputPower || 0;

	$: batteryState = status?.packData?.[0]?.state || 0; // 0 - standby; 1 - charging; 2 - discharging
	$: batteryLevel = status?.electricLevel || 0;

	$: remainingChargeTime = status?.remainInputTime || 0; // in minutes?
	$: remainingDischargeTime = status?.remainOutTime || 0;

	// TODO: Translations
	function getBatteryStateText() {
		switch (batteryState) {
			case 1:
				return $format('text_charging', { default: 'Charging' });
			case 2:
				return $format('text_discharging', { default: 'Discharging' });
			case 0:
			default:
				return $format('text_standby', { default: 'Standby' });
		}
	}

	$: paths = {
		// outputHomePower
		first: { isActive: outputHomePower > 0, animationSeconds: 2 },
		// Solar Input
		second: {
			isActive: solarInputPower > 0,
			animationSeconds: 1.5,
		},
		// Battery
		third: {
			isActive: outputToPackPower > 0 || packInputPower > 0,
			animationSeconds: 1.5,
			isReverse: Number(outputToPackPower) > Number(packInputPower),
		},
	};

	$: cards = [
		// Home
		{
			label: capFirstChar($format('tab_home', { default: 'Home' })),
			tooltip: [],
			icon: 'fas fa-home',
			progress: [{ id: '1', percent: 100, color: 'white' }],
			value1: { text: convert(outputHomePower, 'W') },
		},

		// PV Hub
		{
			label: $format('text_pv_hub', { default: 'PV Hub' }),
			tooltip: [],
			icon: 'fas fa-transformer-bolt',
			progress: [{ id: '1', percent: 100, color: 'white' }],
		},

		// Solar
		{
			label: $format('text_solar_panel', { default: 'Solar Panel' }),
			tooltip: [
				{
					title: $format('text_solar_input', {
						default: 'Solar Input 1',
						values: { index: '1' },
					}),
					description: `${pvPower1} W`,
					dotColor: 'battery',
				},
				{
					title: $format('text_solar_input', {
						default: 'Solar Input 2',
						values: { index: '2' },
					}),
					description: `${pvPower2} W`,
					dotColor: 'battery',
				},
			],
			icon: 'fas fa-solar-panel text-solar',
			progress: [{ id: '1', percent: 100, color: 'solar' }],
			value1: { text: convert(solarInputPower, 'W') },
		},

		// Battery
		{
			label: $format('icon_name_battery', { default: 'Battery' }),
			tooltip: [
				{
					title: `${status?.packNum}x `,
					description: $format('text_batteries', { default: 'batteries' }),
					dotColor: 'battery',
				},
			],
			icon: 'fas fa-car-battery text-battery',
			progress: [{ id: '1', percent: status?.electricLevel || 0, color: 'battery' }],
			value1: {
				text: convert(outputToPackPower, 'W'),
				icon: 'far fa-arrow-right-to-line',
			},
			value2: {
				text: convert(packInputPower, 'W'),
				icon: 'far fa-arrow-left-to-line',
			},
		},
	] as EnergyDistributionZendurePropsTypes['cards'];
</script>

<ParameterCardGroupV2
	class="mb-s"
	sectionParameters={[
		{
			title: `${getBatteryStateText()}`,
			icon: 'fa-solid fa-bolt',
			description: $format('text_battery_state', { default: 'Battery State' }),
		},

		{
			title: `${batteryLevel}%`,
			icon: getBatteryIcon(batteryLevel),
			description: $format('icon_name_battery', { default: 'Battery' }),
		},
	]}
/>

{#if getBatteryStateText() === 'Charging' && remainingChargeTime}
	<ParameterCardV2
		class="mb-s"
		title="{remainingChargeTime} min"
		description={$format('label_remaining_charge_time', {
			default: 'Remaining charge time',
		})}
		size="s"
	/>
{/if}

{#if getBatteryStateText() === 'Discharging' && remainingDischargeTime}
	<ParameterCardV2
		class="mb-s"
		title="{remainingDischargeTime} min"
		description={$format('label_remaining_discharge_time', {
			default: 'Remaining discharge time',
		})}
		size="s"
	/>
{/if}

<ControlsContainer size="s" orientation="vertical" color={$showNewLayout ? 'color-0' : undefined}>
	<svelte:fragment slot="first">
		<EnergyDistributionZendure id="zendure" type="vertical" cards={[...cards].slice(0, 4)} {paths} />
	</svelte:fragment>
</ControlsContainer>
